import React, { useState, useEffect } from "react";
import axios from "axios";
import { Form, Button, Col, Row, Table, Modal } from "react-bootstrap";

const StaticDesignForm = () => {
  const [categories, setCategories] = useState([]);
  const [designSizes, setDesignSizes] = useState([]);
  const [staticDesignData, setStaticDesignData] = useState({
    cat_id: "",
    size_id: "",
    orientation_title: "",
    orientation_opt_1: "",
    orientation_opt_2: "",
    print_title: "",
    print_side_1: "",
    print_side_2: "",
    hanging_option_title: "",
    hanging_option_1: "",
    hanging_option_2: "",
  });
  const [message, setMessage] = useState({ type: "", text: "" });
  const [staticInfoList, setStaticInfoList] = useState([]); // For table data
  const [editData, setEditData] = useState(null); // Data for editing
  const [showEditModal, setShowEditModal] = useState(false); // Modal state

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          "https://drdprints.com/api/get-category"
        );
        setCategories(response.data.categories || []);
      } catch (error) {
        setMessage({ type: "danger", text: "Failed to fetch categories" });
      }
    };

    const fetchDesignSizes = async () => {
      try {
        const response = await axios.get(
          "https://drdprints.com/api/get-staticdesigninfosizes"
        );
        setDesignSizes(response.data.design_info_sizes || []);
      } catch (error) {
        setMessage({ type: "danger", text: "Failed to fetch design sizes" });
      }
    };

    fetchCategories();
    fetchDesignSizes();
  }, []);

  // Fetch static design info for the table
  const fetchStaticDesignInfoList = async (cat_id) => {
    try {
      const response = await axios.get(
        `https://drdprints.com/api/get-staticdesigninfo/${cat_id}`
      );
      setStaticInfoList(response.data.design_info || []);
    } catch (error) {
      setMessage({ type: "danger", text: "Failed to fetch design info" });
    }
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setStaticDesignData((prevData) => ({ ...prevData, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        "https://drdprints.com/api/store-staticdesigninfo",
        staticDesignData
      );
      setMessage({ type: "success", text: "Design info saved successfully!" });
      fetchStaticDesignInfoList(staticDesignData.cat_id); // Refresh table data after saving
    } catch (error) {
      setMessage({ type: "danger", text: "Failed to save design info" });
    }
  };

  // Handle category selection to view table data
  const handleCategorySelect = (e) => {
    const cat_id = e.target.value;
    fetchStaticDesignInfoList(cat_id);
  };

  // Open edit modal with selected row data
  const handleEditClick = (info) => {
    setEditData(info); // Set the selected row data for editing
    setShowEditModal(true); // Show the edit modal
  };

  // Handle edit form submission
  const handleEditSubmit = async () => {
    try {
      await axios.put(
        `https://drdprints.com/api/update-staticdesigninfo/${editData.id}`,
        editData
      );
      setMessage({ type: "success", text: "Design info updated successfully!" });
      setShowEditModal(false); // Close modal after updating
      fetchStaticDesignInfoList(staticDesignData.cat_id); // Refresh table data
    } catch (error) {
      setMessage({ type: "danger", text: "Failed to update design info" });
    }
  };

  // Handle delete functionality
  const handleDeleteClick = async (id) => {
    try {
      await axios.delete(`https://drdprints.com/api/deletestaticdesigninfo/${id}`);
      setMessage({ type: "success", text: "Design info deleted successfully!" });
      fetchStaticDesignInfoList(staticDesignData.cat_id); // Refresh table data
    } catch (error) {
      setMessage({ type: "danger", text: "Failed to delete design info" });
    }
  };

  return (
    <div className="container mt-4">
      <h2>Add Static Design Information</h2>
      {message.text && (
        <div className={`alert alert-${message.type}`}>{message.text}</div>
      )}
      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group controlId="formCategorySelect">
              <Form.Label>Select Category for Form</Form.Label>
              <Form.Control
                as="select"
                name="cat_id"
                value={staticDesignData.cat_id}
                onChange={handleChange}
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.cat_title}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="formSizeSelect">
              <Form.Label>Select Size</Form.Label>
              <Form.Control
                as="select"
                name="size_id"
                value={staticDesignData.size_id}
                onChange={handleChange}
              >
                <option value="">Select a size</option>
                {designSizes.map((size) => (
                  <option key={size.id} value={size.id}>
                    {size.title} - {size.price}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>

        {/* Form fields for orientation, print, and hanging options */}
        <Row>
          <Col md={6}>
            <Form.Group controlId="formOrientationTitle">
              <Form.Label>Orientation Title</Form.Label>
              <Form.Control
                type="text"
                name="orientation_title"
                value={staticDesignData.orientation_title}
                onChange={handleChange}
                placeholder="Enter orientation title"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formOrientationOpt1">
              <Form.Label>Orientation Option 1</Form.Label>
              <Form.Control
                type="text"
                name="orientation_opt_1"
                value={staticDesignData.orientation_opt_1}
                onChange={handleChange}
                placeholder="Enter option 1"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group controlId="formOrientationOpt2">
              <Form.Label>Orientation Option 2</Form.Label>
              <Form.Control
                type="text"
                name="orientation_opt_2"
                value={staticDesignData.orientation_opt_2}
                onChange={handleChange}
                placeholder="Enter option 2"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formPrintTitle">
              <Form.Label>Print Title</Form.Label>
              <Form.Control
                type="text"
                name="print_title"
                value={staticDesignData.print_title}
                onChange={handleChange}
                placeholder="Enter print title"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group controlId="formPrintSide1">
              <Form.Label>Print Side 1</Form.Label>
              <Form.Control
                type="text"
                name="print_side_1"
                value={staticDesignData.print_side_1}
                onChange={handleChange}
                placeholder="Enter print side 1"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formPrintSide2">
              <Form.Label>Print Side 2</Form.Label>
              <Form.Control
                type="text"
                name="print_side_2"
                value={staticDesignData.print_side_2}
                onChange={handleChange}
                placeholder="Enter print side 2"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group controlId="formHangingOptionTitle">
              <Form.Label>Hanging Option Title</Form.Label>
              <Form.Control
                type="text"
                name="hanging_option_title"
                value={staticDesignData.hanging_option_title}
                onChange={handleChange}
                placeholder="Enter hanging option title"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="formHangingOption1">
              <Form.Label>Hanging Option 1</Form.Label>
              <Form.Control
                type="text"
                name="hanging_option_1"
                value={staticDesignData.hanging_option_1}
                onChange={handleChange}
                placeholder="Enter hanging option 1"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group controlId="formHangingOption2">
              <Form.Label>Hanging Option 2</Form.Label>
              <Form.Control
                type="text"
                name="hanging_option_2"
                value={staticDesignData.hanging_option_2}
                onChange={handleChange}
                placeholder="Enter hanging option 2"
              />
            </Form.Group>
          </Col>
        </Row>

        <Button variant="primary" type="submit" className="mt-3" >
          Submit
        </Button>
      </Form>

      <div className="mt-4">
        <h5>View Static Design Information for Selected Category</h5>
        <Form.Group controlId="formCategorySelectTable">
          <Form.Label>Select Category to View Table Data</Form.Label>
          <Form.Control as="select" onChange={handleCategorySelect}>
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.cat_title}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        {/* Table to show static design info for the selected category */}
        {staticInfoList.length > 0 && (
          <Table striped bordered hover className="mt-3">
            <thead>
              <tr>
                <th>Orientation Title</th>
                <th>Orientation Option 1</th>
                <th>Orientation Option 2</th>
                <th>Print Title</th>
                <th>Print Side 1</th>
                <th>Print Side 2</th>
                <th>Hanging Option Title</th>
                <th>Hanging Option 1</th>
                <th>Hanging Option 2</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staticInfoList.map((info) => (
                <tr key={info.id}>
                  <td>{info.orientation_title}</td>
                  <td>{info.orientation_opt_1}</td>
                  <td>{info.orientation_opt_2}</td>
                  <td>{info.print_title}</td>
                  <td>{info.print_side_1}</td>
                  <td>{info.print_side_2}</td>
                  <td>{info.hanging_option_title}</td>
                  <td>{info.hanging_option_1}</td>
                  <td>{info.hanging_option_2}</td>
                  <td>
                    <Button
                      variant="warning"
                      size="sm"
                      className="mb-2"
                      onClick={() => handleEditClick(info)}
                    >
                      Edit
                    </Button>{" "}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteClick(info.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {/* Modal for editing design info */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Design Info</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editData && (
            <>
              <Form.Group controlId="editOrientationTitle">
                <Form.Label>Orientation Title</Form.Label>
                <Form.Control
                  type="text"
                  value={editData.orientation_title}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      orientation_title: e.target.value,
                    })
                  }
                />
              </Form.Group>
              {/* Add other fields like orientation options, print title, etc. */}
              <Button variant="primary" onClick={handleEditSubmit}>
                Save Changes
              </Button>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default StaticDesignForm;
